import { styled } from 'goober';

import { Flexbox } from '@/ui/flexbox/flexbox';
import { colorIcon } from '@/ui/icons/functions';

import { FixedPanelWithShadowLayer } from '@/components/fixed-panel-with-shadow-layer/fixed-panel-with-shadow-layer';
import { useCompany } from '@/features/companies/overview/use-company.query';
import { SkeletonText } from '@/ui/table/infinite-table/skeleton-row';
import { colors } from '@/theme/colors';
import { Icon } from '@/ui/icons/icon';
import { EllipsisTextTooltip } from '@/ui/table/infinite-table/ellipsis-text-tooltip';
import { forwardRef } from 'react';
import { Inline, Stack } from '@/ui/line/line';
import { Button } from '@/ui/button/button';
import { useTranslation } from 'react-i18next';
import { useProjectListingById } from '../use-project-listing-by-id.query';
import { useListingStages } from '@/features/listing-stage/use-listing-stages.query';
import { useIsPendingProjectListingStageMutation } from '../use-pending-project-listing-stage-mutation';
import { useUpdateProjectListingStage } from '../use-update-project-listing-stage.mutation';
import { getListingStageByDefaultName } from '@/features/listing-stage/helpers/get-listing-stage-by-default-name';
import { ListingStageName } from '@/features/listing-stage/types';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { CompanyFundingWidget } from '@/features/companies/overview/funding/company-funding-widget';
import { ProjectFitWidget } from '../project-fit/project-fit-widget';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';
import { PERMISSION } from '@/user/permissions/permission.type';
import { SubHeaderBold } from '@/ui/typography/widgets';
import { ProjectListingDetails } from './project-listing-details';
import { EditorNoteWidget } from '@/features/companies/overview/editor-note-widget';
import { CompanyExternalLinksWidget } from '@/features/companies/overview/external-links/company-external-links-widget';
import { CompanyTagsWidget } from '@/features/companies/overview/tags/company-tags-widget';
import { ListedInProjectsWidget } from '@/features/companies/overview/listed-in-projects/listed-in-projects-widget';
import { InvestorsWidget } from '@/features/companies/overview/investors/investors-widget';
import { ClientsWidget } from '@/features/companies/overview/clients/clients-widget';
import { listingSidebarIdState } from '../../project.state';
import { useSetAtom } from 'jotai';

const WIDGET_HEIGHT = 240;
const LINKS_WIDGET_WIDTH = 240;
const LAST_ROW_HEIGHT = 330;
const WIDGET_WIDTH = 800;
const GAP = 24;

export const ProjectListingSidebar = ({
  listingId,
  onClose,
}: {
  listingId: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation('projects', { keyPrefix: 'listingSidebar' });
  const { data: listing } = useProjectListingById(listingId);
  const companyId = listing?.organization.id;
  const { data: company, isLoading: isCompanyLoading } = useCompany(
    companyId ?? null,
  );
  const isPendingStageMutation =
    useIsPendingProjectListingStageMutation(listingId);
  const { listingStages } = useListingStages();
  const notRelevantStage = getListingStageByDefaultName(
    ListingStageName.notRelevant,
    listingStages,
  );
  const { editProjectListingStage } = useUpdateProjectListingStage();
  const navigateTo = useNavigateTo();
  const setListingSidebarId = useSetAtom(listingSidebarIdState);
  const { hasRequiredPermission } = useUserPermissions();
  const hasEditPermission = hasRequiredPermission(PERMISSION.EDIT_PROJECT);

  const isNotRelevant = listing?.listStatusId === notRelevantStage?.id;

  const moveToNotRelevantStatus = async () => {
    if (!notRelevantStage || isPendingStageMutation) return;

    await editProjectListingStage({
      newProjectListingStageId: notRelevantStage.id,
      projectListingId: listingId,
    });
  };

  return (
    <SidebarWrapper onOutsideClick={onClose}>
      <Header
        name="project-listing-sidebar-header"
        data-testid="project-listing-sidebar-header"
        alignItems="center"
        justify="space-between"
        padding="0 30px"
      >
        <TitleContainer
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          {isCompanyLoading ? (
            <SkeletonText width="150px" />
          ) : (
            <>
              <Inline gap="12px">
                <Icon
                  icon="ArrowRightSections"
                  color={colors.gray.c12}
                  onClick={onClose}
                />
                <EllipsisTextTooltip
                  text={company?.name?.value}
                  Component={<TitleValue />}
                />
              </Inline>
              <Inline gap="12px">
                {!isNotRelevant && hasEditPermission && (
                  <Button
                    disabled={isPendingStageMutation}
                    variant="secondary"
                    onClick={() => moveToNotRelevantStatus()}
                  >{t`markAsNotRelevant`}</Button>
                )}
                {!!listing && (
                  <Button
                    variant="primary"
                    onClick={() => {
                      navigateTo.company({
                        companyId: listing?.organization.id,
                      });
                      setListingSidebarId(null);
                    }}
                  >{t`viewCompany`}</Button>
                )}
              </Inline>
            </>
          )}
        </TitleContainer>
      </Header>
      <ContentWrapper data-testid="project-listing-sidebar-content">
        {companyId ? (
          <Stack gap={`${GAP}px`}>
            <ProjectListingDetails
              companyName={company?.name}
              isDataLoading={isCompanyLoading}
              logoUrl={company?.logoUrl}
              description={company?.detailedDescription}
              keyOffering={company?.keyOffering}
            />
            <Fundings
              header={<SubHeaderBold>{t`fundings`}</SubHeaderBold>}
              companyId={companyId}
              variant="horizontal"
              editable={false}
            />

            <Inline gap={`${GAP}px`}>
              <EditorNote
                companyId={companyId}
                editable={false}
                variant="white"
                height={WIDGET_HEIGHT}
                maxWidth={WIDGET_WIDTH - LINKS_WIDGET_WIDTH - GAP}
              />
              <Links
                companyId={companyId}
                editable={false}
                height={WIDGET_HEIGHT}
              />
            </Inline>

            <ProjectFitWidget
              projectListingId={listingId}
              widgetWidth={WIDGET_WIDTH}
              hasEditPermission={hasEditPermission}
            />

            <Inline gap={`${GAP}px`}>
              <Tags
                header={<SubHeaderBold>{t`tags`}</SubHeaderBold>}
                companyId={companyId}
                editable={false}
                height={360}
                readonlyFullHeight={false}
              />
              <ListedInProjects companyId={companyId} editable={false} />
            </Inline>

            <Inline gap={`${GAP}px`}>
              <Investors
                header={<SubHeaderBold>{t`investors`}</SubHeaderBold>}
                companyId={companyId}
                editable={false}
                height={LAST_ROW_HEIGHT}
                optionsListLimit={5}
              />
              <Clients
                header={<SubHeaderBold>{t`clients`}</SubHeaderBold>}
                companyId={companyId}
                editable={false}
                height={LAST_ROW_HEIGHT}
                optionsListLimit={5}
              />
            </Inline>
          </Stack>
        ) : (
          <Stack gap={`${GAP}px`}>
            <SkeletonWidget width={WIDGET_WIDTH} />
            <SkeletonWidget width={WIDGET_WIDTH / 2} />
          </Stack>
        )}
      </ContentWrapper>
    </SidebarWrapper>
  );
};

const SidebarWrapper = styled(FixedPanelWithShadowLayer)`
  right: 0;

  ${({ theme }) =>
    theme.animations.slideInHorizontally('right', '800px', '0.3s')};

  @media (min-width: 1600px) {
    min-width: unset;
    max-width: 1020px;
  }
`;

const Header = styled(Flexbox)`
  background-color: ${({ theme }) => theme.colors.basics.white};
`;

const TitleContainer = styled(Inline)`
  ${({ theme }) => theme.typography.acrossPlatform.minimizedHeader}
  ${({ theme }) => colorIcon(theme.colors.basics.black)}
  cursor: pointer;
  height: 70px;
`;

const ContentWrapper = styled('div')`
  padding: 24px;
  max-height: calc(100vh - 70px);
  overflow-y: auto;
  overflow-x: hidden;
  min-width: 800px;
  ${({ theme }) => theme.mixins.scrollbar};
`;

export const SkeletonWidget = styled('div')<{ width: number }>`
  height: ${WIDGET_HEIGHT}px;
  width: ${({ width }) => width}px;
  ${({ theme }) => theme.animations.skeletonAnimation()};
`;

const TitleValue = styled('div', forwardRef)`
  ${({ theme }) => theme.mixins.ellipsis}
  max-width: 420px;
`;

const Tags = styled(CompanyTagsWidget)`
  max-width: ${(WIDGET_WIDTH - GAP) / 2}px;
  min-width: ${(WIDGET_WIDTH - GAP) / 2}px;
  min-height: 388px;
  max-height: 388px;
`;

const ListedInProjects = styled(ListedInProjectsWidget)`
  max-width: ${(WIDGET_WIDTH - GAP) / 2}px;
  min-width: ${(WIDGET_WIDTH - GAP) / 2}px;
  min-height: 388px;
`;

const Fundings = styled(CompanyFundingWidget)`
  min-height: unset;
  min-width: ${WIDGET_WIDTH}px;
  max-width: ${WIDGET_WIDTH}px;
`;

const EditorNote = styled(EditorNoteWidget)`
  max-width: ${WIDGET_WIDTH - LINKS_WIDGET_WIDTH - GAP}px;
  width: 100%;
`;

const Links = styled(CompanyExternalLinksWidget)`
  min-width: ${LINKS_WIDGET_WIDTH}px;
  max-width: ${LINKS_WIDGET_WIDTH}px;
`;

const Investors = styled(InvestorsWidget)`
  max-width: ${(WIDGET_WIDTH - GAP) / 2}px;
  min-width: ${(WIDGET_WIDTH - GAP) / 2}px;
`;

const Clients = styled(ClientsWidget)`
  min-height: ${LAST_ROW_HEIGHT}px;
  max-width: ${(WIDGET_WIDTH - GAP) / 2}px;
  min-width: ${(WIDGET_WIDTH - GAP) / 2}px;
`;
